// src/authpages/timetables/AppleCalendarConnect.js
import React, { useState, useEffect } from 'react';
import { Card, Button, Alert } from 'react-bootstrap';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';

const AppleCalendarConnect = ({ onCalendarEvents }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists() && userDoc.data().appleCalendarConnected) {
        setIsConnected(true);
        // Load events if connected
        fetchAppleCalendarEvents();
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error checking Apple Calendar connection:', err);
      setError('Failed to verify calendar connection status');
      setLoading(false);
    }
  };

  const connectAppleCalendar = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to connect Apple Calendar');
        setLoading(false);
        return;
      }
      
      // Update user profile to indicate Apple Calendar is connected
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        appleCalendarConnected: true,
        lastAppleSyncedAt: new Date()
      }, { merge: true });
      
      setIsConnected(true);
      
      // Simulate fetching sample calendar events
      fetchAppleCalendarEvents();
      
      setLoading(false);
    } catch (err) {
      console.error('Error connecting to Apple Calendar:', err);
      setError('Failed to connect to Apple Calendar');
      setLoading(false);
    }
  };

  const disconnectAppleCalendar = async () => {
    try {
      setLoading(true);
      
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to disconnect Apple Calendar');
        setLoading(false);
        return;
      }
      
      // Update user profile to indicate Apple Calendar is disconnected
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        appleCalendarConnected: false
      }, { merge: true });
      
      setIsConnected(false);
      setEvents([]);
      
      // Pass empty events array back to parent
      if (onCalendarEvents) {
        onCalendarEvents([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error disconnecting from Apple Calendar:', err);
      setError('Failed to disconnect from Apple Calendar');
      setLoading(false);
    }
  };

  const fetchAppleCalendarEvents = async () => {
    try {
      // Generate sample events for the next 7 days
      const sampleEvents = generateSampleEvents();
      setEvents(sampleEvents);
      
      // Pass events to parent component
      if (onCalendarEvents) {
        onCalendarEvents(sampleEvents);
      }
    } catch (err) {
      console.error('Error fetching Apple Calendar events:', err);
      setError('Failed to fetch calendar events');
    }
  };

  const generateSampleEvents = () => {
    // Implementation remains the same
    // ...
    return [];
  };

  const syncCalendar = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Refresh events
      fetchAppleCalendarEvents();
      
      // Update last synced timestamp
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        await setDoc(userDocRef, {
          lastAppleSyncedAt: new Date()
        }, { merge: true });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error syncing Apple Calendar:', err);
      setError('Failed to sync calendar');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}
      
      {isConnected ? (
        <div>
          <div className="mb-4">
            <Alert variant="success" className="mb-2">
              Apple Calendar Connected
            </Alert>
            {events.length > 0 && (
              <p>
                <strong>{events.length}</strong> events imported
              </p>
            )}
          </div>
          
          <div className="d-flex justify-content-between">
            <Button 
              variant="outline-primary"
              onClick={syncCalendar}
              disabled={loading}
              className="me-2"
            >
              {loading ? 'Syncing...' : 'Sync Now'}
            </Button>
            
            <Button 
              variant="outline-danger"
              onClick={disconnectAppleCalendar}
              disabled={loading}
            >
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <Button 
            variant="primary" 
            className="w-100 py-2"
            onClick={connectAppleCalendar}
            disabled={loading}
          >
            Connect Apple Calendar
          </Button>
        </div>
      )}
    </>
  );
};

export default AppleCalendarConnect;